import React, { useEffect, useMemo } from 'react';
import { StyledSelectPurchaseOptionsWrapper, selectPurchaseOptionsStyles } from './SelectPurchaseOptions.style';
import { Box, Flex, IconV2, LinkV2, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Option } from './Option';
import type { FormikProps } from 'formik';
import type { EditGiftDailogFields } from '../../types';
import type { PurchaseOption } from '../../EditGift.controller';
import type { GroupGiftPaymentMethodProps } from '../GroupGiftPaymentMethod/GroupGiftPaymentMethod';
import { GroupGiftPaymentMethod } from '../GroupGiftPaymentMethod/GroupGiftPaymentMethod';
import { useCreditCardSetupContext } from '../../../CreditCardSetup/CreditCardSetUp.context';
import { useTranslation } from '@shared/core';
import { TooltipIcon } from '../../EditGiftDialog.styles';
import { Help } from '@assets/index';
import { GROUP_GIFT_HELP_REDIRECTION_LINK } from '../../EditGift.constant';
import { WHITE_GLOVE_RETAILER_DOMAIN_REGEX } from '@apps/registry/common/util/retailers';

interface SelectPurchaseOptionsProps extends GroupGiftPaymentMethodProps {
  isGroupGiftingFeatureEnabled: boolean;
  isGroupGiftDisabled: boolean;
  formik: FormikProps<EditGiftDailogFields>;
  onValueChange: (value: PurchaseOption) => void;
}

export const SelectPurchaseOptions: React.FC<SelectPurchaseOptionsProps> = ({
  isGroupGiftingFeatureEnabled,
  onValueChange,
  formik,
  isGroupGiftDisabled,
  product,
  currency,
  setPaymentMethod,
  setPaymentMethodUsername,
  selectedPaymentMethod,
}) => {
  const { isGroupGiftingEnabled, isGetCashEquivalentEnabled } = formik.values;
  const { payoutAccountInfo, setSetupCreditCard, isGetCashEquivalentSupported } = useCreditCardSetupContext();
  // GCE requires a fully verified payout account (checkout doesn't accept contributions for KYC-later accounts that aren't `readyToUse` yet).
  const isReadyForGetCashEquivalent = payoutAccountInfo?.status === 'readyToUse';
  const { t2 } = useTranslation('sharedRegistry');
  const {
    selectPurchaseOption,
    selectPurchaseExternalTitle,
    selectPurchaseExternalDescription,
    getCashEquivalentTitle,
    usOnly,
    getCashEquivalentDescription,
    makeGroupGift,
    makeGroupGiftDescription,
    learnMore,
    groupGiftingTooltip,
    moreDetails,
  } = t2('giftDialog');

  const isPotentiallyWhiteGloveItem = useMemo(() => {
    const link = formik.values.link || '';

    return WHITE_GLOVE_RETAILER_DOMAIN_REGEX.test(link);
  }, [formik]);

  const isGetCashEquivalentDisabled = isPotentiallyWhiteGloveItem || !isGetCashEquivalentSupported;

  useEffect(() => {
    if (isGetCashEquivalentDisabled && isGetCashEquivalentEnabled) {
      onValueChange('external');
    }
  }, [isGetCashEquivalentDisabled, isGetCashEquivalentEnabled, onValueChange]);

  return (
    <StyledSelectPurchaseOptionsWrapper __css={selectPurchaseOptionsStyles}>
      <TextV2 typographyVariant="label3">{selectPurchaseOption}</TextV2>
      <Option
        labelTitle={selectPurchaseExternalTitle}
        labelDescription={
          <>
            <TextV2
              typographyVariant="label2"
              color="mono12"
            >
              {selectPurchaseExternalDescription}{' '}
              <LinkV2
                color="mono14"
                typographyVariant="label2"
                fontWeight="bolder"
                href="https://help.withjoy.com/knowledge-base/affiliate-external-gift-purchase"
                target="_blank"
              >
                {learnMore}
              </LinkV2>
            </TextV2>
          </>
        }
        value="external"
        checked={!isGetCashEquivalentEnabled && !isGroupGiftingEnabled}
        name="externalPurchase"
        id="externalPurchase"
        onValueChange={onValueChange}
      />
      <Option
        labelTitle={
          <Flex
            flexWrap="wrap"
            alignItems="center"
          >
            <TextV2
              typographyVariant="hed1"
              marginRight={1}
            >
              {getCashEquivalentTitle}
            </TextV2>
            <TextV2
              typographyVariant="label2"
              color="mono12"
            >
              {usOnly}
            </TextV2>
          </Flex>
        }
        labelDescription={
          <>
            <TextV2
              typographyVariant="label2"
              color="mono12"
            >
              {getCashEquivalentDescription}
              <LinkV2
                color="mono14"
                typographyVariant="label2"
                fontWeight="bolder"
                href="https://help.withjoy.com/knowledge-base/get-cash-equivalent-gce"
                target="_blank"
              >
                {learnMore}
              </LinkV2>
            </TextV2>
          </>
        }
        value="cash"
        checked={Boolean(isGetCashEquivalentEnabled)}
        name="cash"
        id="cash"
        onValueChange={newValue => (isReadyForGetCashEquivalent ? onValueChange(newValue) : setSetupCreditCard('fullKyc'))}
        disabled={isGetCashEquivalentDisabled}
      />

      {isGroupGiftingFeatureEnabled && (
        <Option
          labelTitle={
            <Flex
              flexWrap="wrap"
              alignItems="center"
            >
              <TextV2
                typographyVariant="hed1"
                marginRight={1}
              >
                {makeGroupGift}
              </TextV2>
              <TooltipV2
                placement="topStart"
                content={
                  <Box
                    width="480px"
                    typographyVariant="body2"
                  >
                    {groupGiftingTooltip}
                    <TextV2
                      as="span"
                      typographyVariant="body2"
                      marginX="4px"
                    >
                      {moreDetails}
                    </TextV2>
                    <LinkV2
                      color="white"
                      _visited={{ color: 'white' }}
                      typographyVariant="body2"
                      href={GROUP_GIFT_HELP_REDIRECTION_LINK}
                      target="__blank"
                    >
                      {GROUP_GIFT_HELP_REDIRECTION_LINK}
                    </LinkV2>
                  </Box>
                }
              >
                <TooltipIcon>
                  <IconV2
                    size="sm"
                    color="mono8"
                    marginLeft={3}
                  >
                    <Help />
                  </IconV2>
                </TooltipIcon>
              </TooltipV2>
            </Flex>
          }
          labelDescription={
            <>
              <TextV2
                typographyVariant="label2"
                color="mono12"
              >
                {makeGroupGiftDescription}
              </TextV2>
              {isGroupGiftingEnabled && (
                <GroupGiftPaymentMethod
                  setPaymentMethod={setPaymentMethod}
                  setPaymentMethodUsername={setPaymentMethodUsername}
                  selectedPaymentMethod={selectedPaymentMethod}
                  formik={formik}
                  product={product}
                  currency={currency}
                />
              )}
            </>
          }
          value="groupGift"
          checked={Boolean(isGroupGiftingEnabled)}
          name="groupGift"
          id="groupGift"
          onValueChange={onValueChange}
          disabled={isGroupGiftDisabled}
        />
      )}
    </StyledSelectPurchaseOptionsWrapper>
  );
};
